/** The schedules for one bot (or, with scope 'workspace', every schedule in
 * the workspace). `enabled` false means the feature is off for this
 * workspace and nothing about it should render. Changes made here are
 * announced so other mounted lists refetch. */
import { useCallback, useEffect, useRef, useState } from 'react';
import { botTaskSchedulesService, type BotSchedule } from '../../../services/BotTaskSchedulesService';
import {
  BOT_SCHEDULES_CHANGED_EVENT, announceSchedulesChanged, readScheduleCache, scheduleCacheKey, scheduleVersion,
  writeScheduleCache, type BotSchedulesChangedDetail,
} from './botSchedulesStore';

interface Loaded { key: string; enabled: boolean; schedules: BotSchedule[] }

export function useBotSchedules(workspaceId: string, scope: 'mine' | 'workspace', botId?: string) {
  const key = scheduleCacheKey(workspaceId, scope, botId);
  const [data, setData] = useState<Loaded | null>(() => {
    const hit = readScheduleCache(key);
    return hit ? { key, ...hit } : null;
  });
  const [reloads, setReloads] = useState(0);
  const origin = useRef(crypto.randomUUID());

  useEffect(() => {
    const onChanged = (event: Event) => {
      const detail = (event as CustomEvent<BotSchedulesChangedDetail>).detail;
      if (detail?.workspaceId === workspaceId && detail.origin !== origin.current) setReloads((n) => n + 1);
    };
    window.addEventListener(BOT_SCHEDULES_CHANGED_EVENT, onChanged);
    return () => window.removeEventListener(BOT_SCHEDULES_CHANGED_EVENT, onChanged);
  }, [workspaceId]);

  useEffect(() => {
    const hit = readScheduleCache(key);
    if (hit) { setData((previous) => (previous?.key === key ? previous : { key, ...hit })); return; }
    let active = true;
    const startedAt = scheduleVersion(workspaceId);
    botTaskSchedulesService.list({ workspaceId, botId, scope })
      .then((result) => {
        if (!active || scheduleVersion(workspaceId) !== startedAt) return;
        writeScheduleCache(key, workspaceId, result);
        setData({ key, ...result });
      })
      .catch(() => { if (active) setData((previous) => previous?.key === key ? previous : { key, enabled: false, schedules: [] }); });
    return () => { active = false; };
  }, [workspaceId, botId, scope, key, reloads]);

  const current = data?.key === key ? data : null;
  const change = useCallback((update: (list: BotSchedule[]) => BotSchedule[]) => {
    setData((previous) => (previous?.key === key ? { ...previous, schedules: update(previous.schedules) } : previous));
    announceSchedulesChanged(workspaceId, origin.current);
  }, [key, workspaceId]);
  const upsert = useCallback((schedule: BotSchedule) => change((list) =>
    list.some((item) => item.id === schedule.id) ? list.map((item) => (item.id === schedule.id ? schedule : item)) : [schedule, ...list]), [change]);
  const drop = useCallback((id: string) => change((list) => list.filter((item) => item.id !== id)), [change]);

  return { schedules: current?.schedules ?? [], enabled: current?.enabled ?? false, loaded: current !== null, upsert, drop };
}
